// SurveyAnalytics/SurveyAnalyticsList.js
import React, { useEffect, useState } from "react";
import { Table, Button, Space, Tag, message } from "antd";
import { DownloadOutlined, EyeOutlined, EditOutlined, DeleteOutlined, FileExcelOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import useAxiosInstance from "../../../lib/useAxiosInstance";
import ExcelFileImg from "../../../assets/images/ExcelFileImg.png"; 
import DeleteModal from "../../DeleteModal";

const SurveyAnalyticsList = () => {
  const axiosInstance = useAxiosInstance();
  const navigate = useNavigate();

  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(false);

  const [isDeleteModal, setIsDeleteModal] = useState(false);
  const [selectedSurvey, setSelectedSurvey] = useState(null);

  const fetchSurveys = async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.get("api/surveyAnalytics/surveys");
      const list = res.data?.data || [];
      setSurveys(list);
    } catch (error) {
      console.error(error);
      message.error("Failed to load surveys");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSurveys();
  }, []);

  const handleDownload = (record) => {
    const filePath = record.filePath;
    if (!filePath?.publicUrl) {
      message.error("File not available");
      return;
    }
    const a = document.createElement("a");
    a.href = filePath.publicUrl;
    a.setAttribute("download", filePath.name || record.fileName);
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleViewSurvey = (record) => {
    // navigate to detail page that shows map
    navigate(`/survey/${record._id}`);
  };

  const handleEditSurvey = (record) => {
    // up to you: open edit modal / navigate to edit page
    navigate(`/survey-analytics/${record._id}/edit`);
  };


  const openDeleteModal = (record) => {
  setSelectedSurvey(record);
  setIsDeleteModal(true);
};
  const closeDeleteModal = () => {
  setIsDeleteModal(false);
  setSelectedSurvey(null);
};
const confirmDeleteSurvey = async () => {
  if (!selectedSurvey) return;

  try {
    await axiosInstance.delete(
      `api/surveyAnalytics/survey/${selectedSurvey._id}`
    );
    message.success("Survey deleted successfully");
    fetchSurveys();
  } catch (error) {
    console.error(error);
    message.error("Failed to delete survey");
  } finally {
    closeDeleteModal();
  }
};

  const handleDeleteSurvey = async (record) => {
    try {
      await axiosInstance.delete(`api/surveyAnalytics/surveys/${record._id}`);
      message.success("Survey deleted");
      fetchSurveys();
    } catch (error) {
      console.error(error);
      message.error("Failed to delete survey");
    }
  };

  const columns = [
    {
      title: "File",
      dataIndex: "filePath",
      key: "file",
      width: 80,
      render: (_, record) => (
        <span
          onClick={(e) => {
            e.stopPropagation();
            handleDownload(record);
          }}
          style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 8 }}
        >
          {/* Use image or icon */}
          {ExcelFileImg ? (
            <img src={ExcelFileImg} alt="Excel" style={{ width: 24, height: 24 }} />
          ) : (
            <FileExcelOutlined style={{ fontSize: 20 }} />
          )}
          <DownloadOutlined />
        </span>
      ),
    },
    {
      title: "# Questions",
      key: "questionsCount",
      render: (_, record) => record.questionIds?.length || 0,
      width: 120,
    },
    {
      title: "Survey Name",
      dataIndex: "surveyName",
      key: "surveyName",
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      ellipsis: true,
    },
    {
      title: "Clients",
      dataIndex: "client",
      key: "client",
      render: (clients = []) => {
        if (!Array.isArray(clients)) return null;
        return (
          <>
            {clients.map((c) => {
              const label =
                `${c.adminFirstName || ""} ${c.adminLastName || ""}`.trim() ||
                c.email ||
                c._id;
              return (
                <Tag key={c._id} color="blue" style={{ marginBottom: 4 }}>
                  {label}
                </Tag>
              );
            })}
          </>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: 220,
      render: (_, record) => (
        <Space>
          <Button
            size="small"
            icon={<EyeOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              handleViewSurvey(record);
            }}
          >
            View
          </Button>
          {/* <Button
            size="small"
            icon={<EditOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              handleEditSurvey(record);
            }}
          >
            Edit
          </Button> */}
          <Button
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              openDeleteModal(record);            
            }}
          >
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <>
    <div className="survey-list-page">
      <h2>Surveys</h2>
      <Table
        rowKey="_id"
        loading={loading}
        dataSource={surveys}
        columns={columns}
        onRow={(record) => ({
          onClick: () => handleViewSurvey(record), // click row => view
        })}
      />
    </div>
    <DeleteModal
      isDeleteModal={isDeleteModal}
      showModalDelete={closeDeleteModal}
      handleDelete={confirmDeleteSurvey}
      textheading="Delete Survey"
      action="delete"
      deleteTitle={selectedSurvey?.surveyName}
      deleteBtn="Delete"
    />
    </>
  );
};

export default SurveyAnalyticsList;
